import { useCallback, useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebaseClient";
import { logError } from "../lib/logger";
import { formatDraftTimestamp } from "../lib/commandCenterDealDraft";
import { toTaskQueueLoadDisplayError } from "../lib/taskQueueLoadErrors";

const TASK_COLLECTION = "buyer_review_tasks";

export function sortBuyerTasksByNewest(tasks) {
  return [...tasks].sort((a, b) => {
    const aMs = a.created_at?.toDate?.()?.getTime?.() || Date.parse(a.created_at) || 0;
    const bMs = b.created_at?.toDate?.()?.getTime?.() || Date.parse(b.created_at) || 0;
    return bMs - aMs;
  });
}

/**
 * Load active Command Center buyer review tasks for the signed-in admin/buyer.
 */
export function useBuyerReviewTasks(userId, enabled) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadTasks = useCallback(async () => {
    if (!enabled || !userId) {
      setTasks([]);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const q = query(
        collection(db, TASK_COLLECTION),
        where("source", "==", "command_center"),
        where("task_type", "in", ["restocking_review", "transfer_review"]),
        where("status", "==", "draft"),
        where("created_by_uid", "==", userId)
      );
      const snap = await getDocs(q);
      const items = snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setTasks(sortBuyerTasksByNewest(items));
    } catch (err) {
      logError("Failed to load buyer review tasks", err);
      setError(toTaskQueueLoadDisplayError(err, { context: "buyer_review_tasks" }));
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, userId]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  return { tasks, loading, error, reloadTasks: loadTasks, formatTaskTimestamp: formatDraftTimestamp };
}
